import { buttonStyle } from 'components/buttons/buttonStyle';
import ContactPickerModal from 'components/forms/form-components/select-picker/ContactPickerModal';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import InfoTooltip from 'components/InfoTooltip';
import { ModalFooter } from 'components/modals/ModalFooter.tsx';
import { ModalHeader } from 'components/modals/ModalHeader.tsx';
import ContactDynamicSelectPicker from 'components/party/ContactDynamicSelectPicker';
import { Button } from 'components/ui/button';
import { useForm } from 'react-hook-form';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { AffiliationForm } from 'types/employees';
import { t } from 'utils/getTranslations';

interface Props {
    onClose: () => void;
    onSave?: (affiliation: AffiliationForm) => void;
    onDelete?: () => void;
    initialData: AffiliationForm;
    titleId?: string;
}

const AffiliationModal = (props: Props) => {
    const { titleId = 'employees.affiliations.form.edit', initialData, onSave, onClose, onDelete } = props;

    const { handleSubmit, control, setValue } = useForm<AffiliationForm>({
        defaultValues: initialData,
        values: initialData,
    });

    const handleOnChange = (data: AffiliationForm) => {
        if (onSave) onSave(data);
        onClose();
    };

    const handleOnDelete = () => {
        if (onDelete) onDelete();
        onClose();
    };

    const onSaveAffiliation = () => {
        handleSubmit((data) => handleOnChange(data))();
    };

    const deleteButton = onDelete ? (
        <Button
            {...buttonStyle({ variant: 'outlined', color: 'error' }, 'text-sm font-normal')}
            key="delete"
            onClick={() => handleOnDelete()}
        >
            {t('delete')}
        </Button>
    ) : undefined;

    return (
        <>
            <ModalHeader title={t(titleId)} onCancel={onClose} />
            <div className="h-full overflow-y-auto px-8 py-[38px] max-sm:px-4 max-sm:py-6">
                <div className="flex flex-col gap-4">
                    <ContactDynamicSelectPicker
                        setValue={setValue}
                        control={control}
                        label="employees.affiliations.form.contact"
                        required
                        name="party"
                        paginated={{
                            fetch: useLazyGetMinContactsListWithFiltersQuery,
                            searchField: ['name', 'partyNumber'],
                        }}
                        PickerModalComponent={ContactPickerModal}
                        placeholder="employees.affiliations.form.select-contact"
                    />
                    <TextAreaInput
                        name="notes"
                        control={control}
                        label="employees.affiliations.form.notes"
                        placeholder="employees.affiliations.form.notes-placeholder"
                        tooltip={<InfoTooltip text="employees.affiliations.form.notes-tooltip" />}
                        maxLength={1000}
                    />
                </div>
            </div>
            <ModalFooter extraButton={deleteButton} onSave={onSaveAffiliation} onCancel={onClose} />
        </>
    );
};

export default AffiliationModal;
